import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Sparkles, Music, Users } from 'lucide-react';
import { api, cache } from '../services/api';
import ArtistCard from '../components/ArtistCard';
import SongListRow from '../components/SongListRow';
import SEO from '../components/SEO';

// ── Skeleton card components ────────────────────────────────────────────────
function SkeletonSong() {
  return (
    <div className="bg-neutral-900/60 rounded-2xl p-3 animate-pulse">
      <div className="w-full aspect-video bg-neutral-800 rounded-xl mb-3" />
      <div className="h-3 bg-neutral-800 rounded w-3/4 mb-2" />
      <div className="h-2 bg-neutral-800 rounded w-1/2" />
    </div>
  );
}

function SkeletonArtist() {
  return (
    <div className="flex flex-col items-center gap-2 animate-pulse">
      <div className="w-20 h-20 md:w-28 md:h-28 bg-neutral-800 rounded-full" />
      <div className="h-2 bg-neutral-800 rounded w-16" />
      <div className="h-2 bg-neutral-800 rounded w-12" />
    </div>
  );
}

function Home() {
  const [songs, setSongs] = useState(() => cache.get('home_songs') || []);
  const [artists, setArtists] = useState(() => cache.get('home_artists') || []);
  const [loading, setLoading] = useState(songs.length === 0);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const isMounted = useRef(true);

  const pills = ['All', 'Folk', 'Romantic', 'Remix', 'Traditional', 'Dance', 'Podcasts', 'Energize'];

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [songsRes, artistsRes] = await Promise.all([
        api.getSongs({ limit: 16 }).catch(() => []),
        api.getArtists({ limit: 12 }).catch(() => [])
      ]);

      if (!isMounted.current) return;

      // Only update state if data actually changed (avoid re-render flicker)
      setSongs(prev => {
        if (JSON.stringify(prev.map(s => s.id)) === JSON.stringify(songsRes.map(s => s.id))) return prev;
        cache.set('home_songs', songsRes);
        return songsRes;
      });
      setArtists(prev => {
        if (JSON.stringify(prev.map(a => a.id)) === JSON.stringify(artistsRes.map(a => a.id))) return prev;
        cache.set('home_artists', artistsRes);
        return artistsRes;
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    // If we have cached data, show it immediately, then refresh silently
    if (songs.length > 0) {
      fetchData(true); // silent background refresh
    } else {
      fetchData(false);
    }
    return () => { isMounted.current = false; };
  }, [fetchData]); // eslint-disable-line

  // Show the out-of-#root LCP picture on the home page and hide it elsewhere.
  // The picture lives in index.html so React's createRoot doesn't tear it
  // down — that's what locks LCP to the static paint time.
  useEffect(() => {
    const el = document.getElementById('lcp-bg');
    if (el) el.style.display = 'block';
    return () => {
      const el2 = document.getElementById('lcp-bg');
      if (el2) el2.style.display = 'none';
    };
  }, []);

  const filteredTrending = selectedCategory === 'All'
    ? songs.slice(0, 8)
    : songs.filter(s => s.category?.toLowerCase() === selectedCategory.toLowerCase()).slice(0, 8);

  const filteredLatest = selectedCategory === 'All'
    ? songs.slice(0, 8)
    : songs.filter(s => s.category?.toLowerCase() === selectedCategory.toLowerCase()).slice(0, 8);

  const showSkeletonSongs = loading && songs.length === 0;
  const showSkeletonArtists = loading && artists.length === 0;

  return (
    <div className="p-4 md:p-8 max-w-[1500px] mx-auto min-h-screen">
      <SEO
        title="Haryanvi Songs, Artists & Music"
        description="Hariwood is the #1 platform to discover Haryanvi songs, singers, artists, DJs and folk music. Stream new Haryanvi hits and explore Haryana's music culture."
        path="/"
      />
      {/* Premium Hero Banner */}
      <section className="mb-6 md:mb-10">
        <div className="relative overflow-hidden rounded-[1.5rem] md:rounded-[3rem] min-h-[220px] md:min-h-[450px] flex items-center p-5 md:p-16 shadow-2xl border border-neutral-800/20 group">
          {/* Hero <picture> lives at #lcp-bg in index.html (outside #root)
              so React's createRoot doesn't tear it down on mount and LCP
              stays locked to the static paint time. Only the gradient
              overlay and text content render here. */}
          <div className="absolute inset-0 z-0 pointer-events-none bg-gradient-to-r from-black/95 via-black/40 to-transparent" />

          <div className="relative z-10 max-w-2xl text-left mx-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-500 text-[8px] md:text-xs font-black mb-3 md:mb-6 border border-yellow-500/20">
              <Sparkles className="w-3 h-3" />
              THE #1 HARYANVI PLATFORM
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-8xl font-black mb-1 md:mb-4 leading-none text-white tracking-tighter drop-shadow-2xl">
              HARIWOOD
            </h1>
            <p className="text-sm md:text-2xl font-black mb-6 md:mb-10 max-w-lg">
              <span className="text-yellow-500 drop-shadow-lg leading-tight block">
                The Sound of Real Haryanvi Culture
              </span>
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
              <Link to="/explore" className="w-[180px] sm:w-auto btn-primary inline-flex items-center justify-center gap-2 px-6 md:px-10 py-3 md:py-4 text-xs md:text-lg font-black shadow-xl shadow-yellow-500/10 hover:scale-[1.02] transition-transform">
                Start Listening <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
              </Link>
              <Link to="/artists" className="w-[180px] sm:w-auto btn-secondary inline-flex items-center justify-center gap-2 px-6 md:px-10 py-3 md:py-4 text-xs md:text-lg font-black bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all text-white">
                Browse Crew <Users className="w-4 h-4 md:w-5 md:h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="space-y-12 md:space-y-24">
        {/* Category Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-3 sticky top-16 md:top-20 z-20 backdrop-blur-xl bg-black/60 py-4 md:py-6 px-4 -mx-4 md:mx-0 md:px-0">
          {pills.map((pill) => (
            <button
              key={pill}
              onClick={() => setSelectedCategory(pill)}
              className={`px-6 py-2 md:py-2.5 text-[11px] md:text-sm font-black rounded-full transition-all whitespace-nowrap border ${
                selectedCategory === pill
                  ? 'bg-yellow-500 text-black border-yellow-500 shadow-lg shadow-yellow-500/20'
                  : 'bg-neutral-900/40 hover:bg-neutral-800 text-neutral-400 border-neutral-800/10'
              }`}
            >
              {pill}
            </button>
          ))}
        </div>

        {/* Quick Picks */}
        <section>
          <div className="flex items-center justify-between mb-8 px-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
              <h2 className="text-xl md:text-4xl font-black tracking-tight uppercase">Quick picks</h2>
            </div>
            <Link to="/explore" className="text-yellow-500 text-[10px] md:text-sm font-bold uppercase tracking-widest hover:underline px-4 py-2 bg-yellow-500/5 rounded-full transition-colors">
              View All
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 px-1">
            {showSkeletonSongs
              ? Array.from({ length: 8 }).map((_, i) => <SkeletonSong key={i} />)
              : filteredTrending.map((song) => (
                  <SongListRow key={song.id} song={song} playlist={filteredTrending} />
                ))}
          </div>
        </section>

        {/* Latest Release */}
        <section>
          <div className="flex items-center gap-3 mb-8 px-1">
            <div className="w-10 h-10 rounded-full bg-pink-500/10 flex items-center justify-center">
              <Music className="w-5 h-5 text-pink-500" />
            </div>
            <h2 className="text-xl md:text-4xl font-black tracking-tight uppercase">Latest Release</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 px-1">
            {showSkeletonSongs
              ? Array.from({ length: 8 }).map((_, i) => <SkeletonSong key={i} />)
              : filteredLatest.map((song) => (
                  <SongListRow key={song.id} song={song} playlist={filteredLatest} />
                ))}
          </div>
        </section>

        {/* Artist Crew */}
        <section className="bg-neutral-900/20 p-5 md:p-12 rounded-[2rem] md:rounded-[4rem] border border-neutral-800/20 shadow-2xl">
          <div className="flex items-center justify-between mb-8 md:mb-10 px-1">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-yellow-500" />
              </div>
              <h2 className="text-xl md:text-5xl font-black tracking-tight uppercase">Artist Crew</h2>
            </div>
            <Link 
              to="/artists" 
              className="p-2.5 md:p-3 bg-yellow-500 text-black rounded-full hover:scale-110 transition-transform shadow-xl shadow-yellow-500/20"
              aria-label="View artist crew"
            >
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-10">
            {showSkeletonArtists
              ? Array.from({ length: 12 }).map((_, i) => <SkeletonArtist key={i} />)
              : artists.map((artist) => (
                  <ArtistCard key={artist.id} artist={artist} />
                ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Home;
